import {CommandKeybindingHint, ScopedCommands} from '@github-ui/ui-commands'
import {AnchoredOverlay, Button, type ButtonProps, FormControl, TextInput, type TextInputProps} from '@primer/react'
import {type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import {LABELS} from './constants/labels'
import {FALLBACK_WIDTH, textValidation} from './constants/text'
import textStyles from './IssueFieldTextToken.module.css'
import {renderTextWithLinks} from './utils/TextUrlRenderer'

type IssueFieldTextEditorBaseProps = {
  fieldId: string
  fieldName: string
  initialValue: string
  onCommit: (fieldId: string, value: string | null) => void
  validate?: (value: string) => string | null
  inputProps?: TextInputProps
  isDraft?: boolean
  isNumber?: boolean
  /// If set to true, then the text editor should render in open state
  shouldOpen?: boolean
}

export type IssueFieldTextEditorProps = IssueFieldTextEditorBaseProps & {
  errorPresent?: boolean
  className?: string
  readonly: boolean
}

export type IssueFieldTextEditorInternalProps = IssueFieldTextEditorBaseProps & {
  renderAnchor: (
    buttonProps: ButtonProps | undefined,
    ref: RefObject<HTMLButtonElement>,
    displayTitle: string,
  ) => React.JSX.Element
  overrideWidth?: string
  onClose?: () => void
}

const defaultValidate = (value: string) => textValidation(value)

export const IssueFieldTextEditor = ({
  initialValue,
  validate = defaultValidate,
  className,
  errorPresent = false,
  readonly,
  ...props
}: IssueFieldTextEditorProps) => {
  const anchor = (buttonProps: ButtonProps | undefined, ref: RefObject<HTMLButtonElement>, displayTitle: string) => (
    <SectionHeader
      ref={ref}
      title={displayTitle}
      readonly={readonly}
      buttonProps={buttonProps}
      errorPresent={errorPresent}
    />
  )

  const sectionHeader = (
    <IssueFieldTextEditorInternal initialValue={initialValue} validate={validate} renderAnchor={anchor} {...props} />
  )
  return (
    <Section sectionHeader={sectionHeader} className={className}>
      {!initialValue ? (
        <span className={textStyles.noValueContainer}>{LABELS.setValue}</span>
      ) : (
        <span className={textStyles.issuesFieldText}>{renderTextWithLinks(initialValue)}</span>
      )}
    </Section>
  )
}

export const IssueFieldTextEditorInternal = ({
  fieldId,
  fieldName,
  initialValue,
  onCommit,
  shouldOpen = false,
  validate = defaultValidate,
  inputProps,
  isDraft = false,
  isNumber = false,
  renderAnchor,
  overrideWidth,
  onClose: onClosed,
}: IssueFieldTextEditorInternalProps) => {
  // Text editor is clean while the user hasn't made any changes,
  // prevent navigating away if there are unsaved changes
  const [isClean, setIsClean] = useState<boolean>(true)
  // Track whether the field has unsaved changes
  const [isUnsaved, setIsUnsaved] = useState<boolean>(false)

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isUnsaved) {
        event.preventDefault()
        event.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isUnsaved])

  // Input field state
  const [inputValue, setInputValue] = useState<string>(initialValue)

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target
    setInputValue(value)
    if (value === initialValue) {
      setIsClean(true)
    } else {
      setIsClean(false)
    }
  }

  // Validation state
  const validationMessage = useMemo<string | null>(
    () => (isClean ? null : validate(inputValue)),
    [isClean, inputValue, validate],
  )
  const preventSubmit = useMemo<boolean>(() => isClean || !!validationMessage, [isClean, validationMessage])

  // Automatically open the editor
  const [isEditing, setIsEditing] = useState<boolean>(shouldOpen)

  // Handlers for the toolbar
  const onRemove = useCallback(() => {
    onCommit(fieldId, isNumber ? null : '')
    setIsUnsaved(false)
  }, [fieldId, isNumber, onCommit])
  const onCancel = useCallback(() => {
    if (initialValue) {
      setInputValue(initialValue)
      setIsEditing(false)
      setIsClean(true)
      setIsUnsaved(false) // Cancel explicitly discards changes
    } else {
      onRemove()
    }
  }, [initialValue, onRemove])
  const onUpdate = useCallback(() => {
    onCommit(fieldId, inputValue)
    setIsEditing(false)
    setIsClean(true)
    setIsUnsaved(false)
  }, [fieldId, onCommit, inputValue])

  // Handlers for the overlay
  const onOpen = useCallback(() => setIsEditing(true), [])
  const onClose = useCallback(() => {
    onClosed?.()
    if (isDraft) {
      onRemove()
      return
    }
    if (!isClean) {
      setIsUnsaved(true)
    }
    setIsEditing(false)
  }, [isClean, isDraft, onClosed, onRemove])

  // Keyboard commands
  const commandProps = useMemo(
    () => ({
      commands: {
        'github:cancel': onCancel,
        'github:submit-form': preventSubmit ? undefined : onUpdate,
      },
      keys: {
        'github:cancel': ['Escape'],
        'github:submit-form': ['Enter'],
      },
    }),
    [preventSubmit, onCancel, onUpdate],
  )

  // Overlay styles to match header
  const headerRef = useRef<HTMLButtonElement>(null)
  const [calculatedWidth, setCalculatedWidth] = useState(FALLBACK_WIDTH)

  // Calculate width when overlay opens
  useEffect(() => {
    if (isEditing && headerRef.current) {
      const width = headerRef.current.getBoundingClientRect().width
      if (width > 0) {
        setCalculatedWidth(`${width}px`)
      }
    }
  }, [isEditing])

  // Calculate the display title for the field
  const displayTitle = useMemo(() => {
    return isUnsaved ? `${fieldName} (edited)` : fieldName
  }, [fieldName, isUnsaved])

  return (
    <AnchoredOverlay
      open={isEditing}
      anchorRef={headerRef}
      renderAnchor={props => renderAnchor(props, headerRef, displayTitle)}
      onClose={onClose}
      onOpen={onOpen}
      overlayProps={{style: {width: overrideWidth ?? calculatedWidth}}}
    >
      <ScopedCommands {...commandProps}>
        <div className={textStyles.issuesFieldTextInputContainer}>
          <FormControl>
            <FormControl.Label visuallyHidden>{LABELS.textEditorLabel(fieldName)}</FormControl.Label>
            <TextInput
              block
              value={inputValue}
              placeholder={LABELS.textEditorPlaceholder}
              onChange={handleTextChange}
              {...inputProps}
            />
            {validationMessage && (
              <FormControl.Validation variant="error" className={textStyles.issuesFieldTextError}>
                {validationMessage}
              </FormControl.Validation>
            )}
          </FormControl>
        </div>
        <div
          className={textStyles.issuesFieldToolbar}
          role="toolbar"
          aria-label={LABELS.textEditorToolbarLabel(fieldName)}
        >
          <div className={textStyles.issuesFieldToolbarActions}>
            <Button
              size="small"
              onClick={onCancel}
              className={textStyles.actionButton}
              trailingVisual={<CommandKeybindingHint commandId="github:cancel" />}
            >
              {LABELS.textEditorCancelButton}
            </Button>
            <Button
              size="small"
              variant="primary"
              onClick={onUpdate}
              aria-disabled={preventSubmit}
              aria-label={LABELS.textEditorUpdateButtonAriaLabel(fieldName)}
              className={textStyles.actionButton}
              trailingVisual={
                <CommandKeybindingHint
                  commandId="github:submit-form"
                  variant={preventSubmit ? 'normal' : 'onPrimary'}
                />
              }
            >
              {LABELS.textEditorUpdateButton}
            </Button>
          </div>
        </div>
      </ScopedCommands>
    </AnchoredOverlay>
  )
}

try{ IssueFieldTextEditor.displayName ||= 'IssueFieldTextEditor' } catch {}
try{ IssueFieldTextEditorInternal.displayName ||= 'IssueFieldTextEditorInternal' } catch {}